import { cloneDeep, isEmpty, noop , get , last} from "lodash";
import React, { createContext, useContext, useState} from "react";
import ReportService from "@services/report.service";
import { colFilterMiddleWare } from "@components/filters/colFilters";
export type UseLabsProps = {};

export type UseColConfig = (props: UseLabsProps) => {
  updateTableConfig?: any; 
  toggleDrawer: () => void;
  resetDefaultTableConfig?: any; 
  onCloseConfigDrawer?: () => void;
  isOpenDrawer: boolean;
  tempTableConfig: any[];
  tableConfigCallback: (data: any) => void;
  tableConfigData : any[];
  setTableConfigData: (data: any) => void;
  columnFilterCheck: any;
  isFilterModal: { open: boolean; action: string; data: any; onOk?: any; colNames?: Array<string> };
  filterModalOk: () => void;
  filterModalCancel: () => void;
  getExpandableColumnTitle : (header: string) => void;
  expandColumn : (header: string) => void;
};

const getColumn = (columns: any, key: string, parentColRequired = false) => {
  let column: any = {};
  columns.forEach((col: any) => {
    if (col.dataIndex === key) {
      column = col;
      return;
    } else if (col.subColumns && col.subColumns.length > 0) {
      col.subColumns.forEach((c: any) => {
        if (c.dataIndex === key) {
          column = parentColRequired ? col : c;
          return;
        }
      });
    }
  });
  return column;
};

export const useColConfig: UseColConfig = function (props) {
  const [tempTableConfig, tempTableConfigHandler] = useState<Array<any>>([]);
  const [tableConfigData, setTableConfigData] = useState<Array<any>>([]);
  const [isOpenDrawer, toggleDrawerHandler] = useState<boolean>(false);

  const [isFilterModal, setIsFilterModal] = useState<{ open: boolean; action: string; data: any; onOk?: any; colNames?: Array<string> }>({ open: false, action: "", data: {}, colNames: [] });

  const filterModalOk = () => {
    if (isFilterModal.onOk) {
      isFilterModal.onOk();
    }
    setIsFilterModal({ open: false, action: "", data: {}, colNames: [] });
  };

  const filterModalCancel = () => {
    setIsFilterModal({ open: false, action: "", data: {}, colNames: [] });
  };

  // toggleDrawer function is used to toggle the column config drawer
  function toggleDrawer() {
    console.log("toggleDrawer");
    toggleDrawerHandler((prev: boolean) => !prev);
  }

  // columnFilterCheck function is used to check if the column that we are trying to remove in the column config has filters applied
  const columnFilterCheck = ({ config = tempTableConfig, filterInfo = {}, action = "", data = {}, onOk = undefined }): boolean => {
    let flag = false;
    let cols: Array<string> = [];
    Object.keys(filterInfo).forEach((column_key: string) => {
      let configCol = getColumn(config, column_key, true);
      let prevConfigCol = getColumn(tempTableConfig, column_key, true);
      if (!isEmpty(configCol) && !configCol.isSelected && prevConfigCol?.isSelected) {
        cols.push(configCol.displayName);
        setIsFilterModal({ open: true, action: action, data: data, onOk: onOk, colNames: cols });
        flag = true;
      }
    });
    return flag;
  };

  // updateTableConfig function is used to update the isSelected state of column in the db and call the columns api again
  const updateTableConfig = React.useCallback(
    ({ columnListData, getColumns, reportName }: any) => {
      const columns: any = [];
      tempTableConfig?.forEach((col: any) => {
        const arr = columnListData.filter((cld: any) => col.dataIndex === cld.key);
        if (arr && arr.length > 0 && col.isSelected) columns.push({ column_id: arr[0].column_id, order: col.sequence });
      });

      ReportService.updateColumns(columns, reportName).subscribe({
        next: (v) => {
          getColumns();
          setTableConfigData(tempTableConfig);
        },
        error: (e) => {},
      });

      toggleDrawer();
    },
    [tempTableConfig]
  );

  // resetDefaultTableConfig function is used to update the tableConfig on when reset column layout is clicked or cancel is clicked without clicking on update
  const resetDefaultTableConfig = ({ action = "default", filteredInfo = {} }) => {
    if (action === "default") {
      let columnData = cloneDeep(tempTableConfig);
      columnData = columnData?.map((col: any) => {
        if (col.isDraggable) {
          if (col.isDefault === "Y") {
            col.sequence = col.defaultDisplayOrder;
            col.isSelected = true;
            col.draggable = true;
          } else {
            col.sequence = 0;
            col.isSelected = false;
            col.draggable = false;
          }
        } else {
          col.sequence = col.order;
          col.isSelected = true;
          col.draggable = true;
        }
        return col;
      });
      colFilterMiddleWare(columnFilterCheck, { config: columnData, filterInfo: filteredInfo }, () => {
        tempTableConfigHandler(columnData);
      });
    } else {
      tempTableConfigHandler(tableConfigData);
    }
  };

  /* onCloseConfigDrawer function is used to close config drawer and if the user added and removed the columns and didnt click on update 
  columns will be updated to previous state */
  const onCloseConfigDrawer = () => {
    toggleDrawer();
    resetDefaultTableConfig({ action: "revert" });
  };

  const tableConfigCallback = (data: any) => {
    tempTableConfigHandler(data);
  };

  function getExpandableColumnTitle(header: string){
    return (
      <div className="expandable-column-header">
        {header.toUpperCase()}
        <i className="btn-icon icon-chevron-solid-v-col" onClick={() => expandColumn(header)}></i>
      </div>
    );
  };

  function expandColumn(header: string){
    setTableConfigData((old: any) =>
      old?.map((item: any) => {
        if (item.subColumns.length > 0 && get(last(item.subColumns), 'parentTableHeaderName')  === header) {
          if (item.expanded) {
            item.expanded = false;
            item.children = [];
          } else {
            item.expanded = true;
            item.children = [...item.subColumns];
          }
        }
        return item;
      })
    );
  };


  return {
    updateTableConfig,
    toggleDrawer,
    resetDefaultTableConfig,
    onCloseConfigDrawer,
    isOpenDrawer,
    tempTableConfig,
    tableConfigCallback,
    tableConfigData,
    setTableConfigData,
    columnFilterCheck,
    isFilterModal,
    filterModalOk,
    filterModalCancel,
    getExpandableColumnTitle,
    expandColumn
  };
};

export const ColConfigContext = createContext<ReturnType<UseColConfig>>({
  isOpenDrawer: false,
  toggleDrawer: noop,
  tempTableConfig: [],
  tableConfigCallback: noop,
  tableConfigData : [],
  setTableConfigData: noop,
  columnFilterCheck: noop,
  isFilterModal: { open: false, action: "", data: {}, colNames: [] },
  filterModalOk: noop,
  filterModalCancel: noop,
  getExpandableColumnTitle : noop,
  expandColumn : noop
});

export const ColConfigProvider: React.FC<UseLabsProps> = function ({ children, ...props }) {
  const colConfigValue = useColConfig(props);

  return <ColConfigContext.Provider value={colConfigValue}>{children}</ColConfigContext.Provider>;
};

export const useColConfigContext = () => useContext(ColConfigContext);
